import React from 'react';
import { useNavigate } from 'react-router-dom';
import Icon from '../../../components/AppIcon';
import Logo from '../../../components/Logo';
import { contactInfo } from 'config';
import { FALLBACK_DOWNLOADS } from '../../../utils/desktopRelease';

const Footer = () => {
  const currentYear = new Date().getFullYear();

  const footerLinks = {
    product: [
      { name: 'Tüm Modüller', href: '/moduller' },
      { name: 'QR Menü (Ücretsiz)', href: '/qr-menu' },
      { name: 'QR Menü Pro', href: '/qr-order' },
      { name: '3D/AR Menü', href: '/ar-menu' },
      { name: 'POS Sistemi', href: '/pos-system' },
      { name: 'Online Sipariş & Paket Servis', href: '/online-order' },
      { name: 'Kurye Takip', href: '/courier-system' },
      { name: 'Sadakat Puanı', href: '/loyalty' },
      { name: 'Cari, Finans & Ortaklık', href: '/cari-finans' },
      { name: 'Yapay Zeka', href: '/ai-system' },
      { name: 'Son Eklenen Özellikler', href: '#yenilikler' },
      { name: 'Sıkça Sorulan Sorular', href: '#faq' }
    ],
    solutions: [
      { name: 'Tüm Çözümler', href: '/solutions' },
      { name: 'Kafeler', href: '/solutions/cafe' },
      { name: 'Restoranlar', href: '/solutions/restaurant' },
      { name: 'Fast Food', href: '/solutions/fastfood' },
      { name: 'Dürümcüler', href: '/solutions/doner' },
      { name: 'Pastane/Fırın', href: '/solutions/bakery' },
      { name: 'Zincir Marketler', href: '/solutions/chain' }
    ],
    support: [
      { name: 'Bayilik Başvurusu', href: '/franchise' },
      { name: 'Dökümantasyon (PDF)', href: '/assets/docs/CafeOtomasyonu-Tanitim-ve-Kullanim.pdf' },
      // Kullanım rehberi. Buradaki bağlantı e-postaya gidiyordu: "yardım"
      // arayan kullanıcı kendi cevabını bulabileceği bir sayfa yerine bize
      // yazmak zorunda kalıyordu — rehberin var olma sebebi tam olarak bu.
      { name: 'Kullanım Rehberi', href: '/guide' },
      { name: 'Kasa Uygulamasını İndir', href: '/download' },
      { name: 'Canlı Destek', href: 'https://wa.me/905075880481' },
      { name: 'API Dokümantasyonu', href: 'mailto:info@cafeotomasyonu.com' },
      { name: 'Sistem Durumu', href: 'https://status.cafeotomasyonu.com' },
      { name: 'Güvenlik', href: 'mailto:info@cafeotomasyonu.com' }
    ],
    legal: [
      { name: 'Gizlilik Politikası', href: '/privacy-policy' },
      { name: 'Kullanım Şartları', href: '/terms-of-service' },
      { name: 'Çerez Politikası', href: '/cookie-policy' },
      { name: 'KVKK', href: '/kvkk' },
      { name: 'İade Politikası', href: '/refund-policy' },
      { name: 'SLA', href: '/sla' }
    ]
  };

  const socialLinks = [
    { name: 'LinkedIn', icon: 'Linkedin', href: `https://linkedin.com/company/${contactInfo.linkedin}` },
    // { name: 'Twitter', icon: 'Twitter', href: 'https://twitter.com/cafeotomasyonu' },
    { name: 'Instagram', icon: 'Instagram', href: `https://www.instagram.com/${contactInfo.instagram}` },
    { name: 'YouTube', icon: 'Youtube', href: `https://youtube.com/@${contactInfo.youtube}` },
    { name: 'Facebook', icon: 'Facebook', href: `https://facebook.com/${contactInfo.facebook}` }
  ];

  /**
   * ⚠️ Burada "ISO 27001 · PCI DSS · GDPR" sertifika rozetleri vardı; hiçbiri
   * alınmış değil. Sertifika iddiası doğrulanabilir bir şeydir ve yanlışı
   * görüşmede değil denetimde ortaya çıkar. Yerine ÜRÜNDE GERÇEKTEN olan
   * güvenlik uygulamaları yazıyor.
   */
  const securityPractices = [
    { icon: 'Lock', text: 'Şifreli bağlantı (HTTPS)' },
    { icon: 'Building2', text: 'Her işletmenin verisi ayrı' },
    { icon: 'KeyRound', text: 'Kart bilgisi bizde tutulmaz — ödeme iyzico üzerinden' },
    { icon: 'ShieldCheck', text: 'Şifre değişince tüm oturumlar kapanır' }
  ];

  const navigate = useNavigate();

  /**
   * ⚠️ İÇ ROTALAR AYNI SEKMEDE açılmalı.
   *
   * Hepsi `window.open(href, '_blank')` ile açılıyordu: footer'daki her modül
   * bağlantısı yeni bir sekme doğuruyor, ziyaretçi üç modüle bakınca dört
   * sekmeyle kalıyordu. Ayrıca tam sayfa yükleme yapıyordu — SPA yönlendirmesi
   * varken gereksiz. Dış adresler (WhatsApp, durum sayfası, PDF) yeni sekmede
   * kalıyor; oradan geri dönüş beklenmiyor.
   */
  const handleLinkClick = (href) => {
    if (href.startsWith('#')) {
      const element = document.getElementById(href.substring(1));
      if (element) {
        const offsetTop = element.offsetTop - 80;
        window.scrollTo({
          top: offsetTop,
          behavior: 'smooth'
        });
      } else {
        // Footer ana sayfa dışında da çiziliyor (AR sayfası): bölüm o sayfada
        // yoksa bağlantı hiçbir şey yapmıyordu. Ana sayfaya gidip kaydırıyor.
        navigate(`/${href}`);
      }
      return;
    }

    const isExternal = /^(https?:|mailto:|tel:)/.test(href) || href.startsWith('/assets/');
    if (isExternal) {
      window.open(href, '_blank', 'noopener');
      return;
    }

    navigate(href);
  };

  return (
    <footer className="bg-gray-900 text-white">
      {/* Main Footer */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-6 gap-8">
          {/* Company Info */}
          <div className="lg:col-span-2">
            <div className="flex items-center space-x-3 mb-6">
              <Logo size={40} color="white" />
              <span className="font-poppins font-heading-bold text-2xl">
                Cafe Otomasyonu
              </span>
            </div>

            <p className="font-open-sans font-body-normal text-gray-300 mb-6 leading-relaxed">
              Türkiye'nin en kapsamlı işletme otomasyon sistemi. QR menüden yapay zeka analizine,
              çevrimdışı çalışan POS'tan reçete maliyetine ve ortaklık defterine kadar tüm dijital
              ihtiyaçlarınızı tek platformda çözüyoruz.
            </p>

            {/* Contact Info */}
            <div className="space-y-3">
              <div className="flex items-center space-x-3">
                <Icon name="Phone" size={16} className="text-primary" />
                <span className="font-open-sans font-body-normal text-sm text-gray-300">
                  {contactInfo.phone}
                </span>
              </div>

              <div className="flex items-center space-x-3">
                <Icon name="Mail" size={16} className="text-primary" />
                <span className="font-open-sans font-body-normal text-sm text-gray-300">
                  {contactInfo.email}
                </span>
              </div>

              <div className="flex items-center space-x-3">
                <Icon name="MapPin" size={16} className="text-primary" />
                <span className="font-open-sans font-body-normal text-sm text-gray-300">
                  {contactInfo.address}
                </span>
              </div>
            </div>
          </div>

          {/* Product Links */}
          <div>
            <h3 className="font-poppins font-heading-semibold text-lg mb-6">
              Ürünler
            </h3>
            <ul className="space-y-3">
              {footerLinks.product.map((link, index) => (
                <li key={index}>
                  <button
                    onClick={() => handleLinkClick(link.href)}
                    className="font-open-sans font-body-normal text-sm text-gray-300 hover:text-white transition-colors duration-300"
                  >
                    {link.name}
                  </button>
                </li>
              ))}
            </ul>
          </div>

          {/* Solutions Links */}
          <div>
            <h3 className="font-poppins font-heading-semibold text-lg mb-6">
              Çözümler
            </h3>
            <ul className="space-y-3">
              {footerLinks.solutions.map((link, index) => (
                <li key={index}>
                  <button
                    onClick={() => handleLinkClick(link.href)}
                    className="font-open-sans font-body-normal text-sm text-gray-300 hover:text-white transition-colors duration-300"
                  >
                    {link.name}
                  </button>
                </li>
              ))}
            </ul>
          </div>

          {/* Support Links */}
          <div>
            <h3 className="font-poppins font-heading-semibold text-lg mb-6">
              Destek
            </h3>
            <ul className="space-y-3">
              {footerLinks.support.map((link, index) => (
                <li key={index}>
                  <button
                    onClick={() => handleLinkClick(link.href)}
                    className="font-open-sans font-body-normal text-sm text-gray-300 hover:text-white transition-colors duration-300"
                  >
                    {link.name}
                  </button>
                </li>
              ))}
            </ul>
          </div>

          {/* Legal Links */}
          <div>
            <h3 className="font-poppins font-heading-semibold text-lg mb-6">
              Yasal
            </h3>
            <ul className="space-y-3">
              {footerLinks.legal.map((link, index) => (
                <li key={index}>
                  <button
                    onClick={() => handleLinkClick(link.href)}
                    className="font-open-sans font-body-normal text-sm text-gray-300 hover:text-white transition-colors duration-300"
                  >
                    {link.name}
                  </button>
                </li>
              ))}
            </ul>
          </div>
        </div>

        {/* Sosyal medya + kasa uygulaması */}
        <div className="mt-16 pt-8 border-t border-gray-800">
          <div className="grid md:grid-cols-2 gap-8">
            {/* Social Media */}
            <div>
              <h4 className="font-poppins font-heading-semibold text-lg mb-4">
                Sosyal Medya
              </h4>
              <div className="flex items-center space-x-4">
                {socialLinks.map((social, index) => (
                  <button
                    key={index}
                    onClick={() => handleLinkClick(social.href)}
                    className="w-10 h-10 bg-gray-800 hover:bg-primary rounded-lg flex items-center justify-center transition-colors duration-300 group"
                    title={social.name}
                  >
                    <Icon name={social.icon} size={20} className="text-gray-300 group-hover:text-white" />
                  </button>
                ))}
              </div>
            </div>

            {/* Kasa uygulaması — indirme footer'da da görünmeli: müşteri onu
                çoğu zaman destekten değil sitenin altından arıyor. Adresler
                sabit "son sürüm" adresi, yani yayında güncellenmesi gerekmiyor. */}
            <div>
              <h4 className="font-poppins font-heading-semibold text-lg mb-4">
                Kasa Uygulaması
              </h4>
              <div className="flex flex-wrap gap-3">
                <a
                  href={FALLBACK_DOWNLOADS.windows.stableUrl}
                  className="inline-flex items-center gap-3 px-4 py-2.5 rounded-lg bg-gray-800 hover:bg-primary transition-colors duration-300"
                >
                  <Icon name="Monitor" size={20} className="text-gray-200" />
                  <span className="text-left leading-tight">
                    <span className="block text-[11px] text-gray-400">İndir</span>
                    <span className="block font-inter font-accent-semibold text-sm">Windows</span>
                  </span>
                </a>
                <a
                  href={FALLBACK_DOWNLOADS.mac.stableUrl}
                  className="inline-flex items-center gap-3 px-4 py-2.5 rounded-lg bg-gray-800 hover:bg-primary transition-colors duration-300"
                >
                  <Icon name="Laptop" size={20} className="text-gray-200" />
                  <span className="text-left leading-tight">
                    <span className="block text-[11px] text-gray-400">İndir</span>
                    <span className="block font-inter font-accent-semibold text-sm">macOS</span>
                  </span>
                </a>
                <button
                  onClick={() => handleLinkClick('/download')}
                  className="inline-flex items-center gap-1 px-2 py-2.5 font-open-sans text-sm text-gray-300 hover:text-white"
                >
                  Kurulum ve gereksinimler
                  <Icon name="ArrowRight" size={14} />
                </button>
              </div>
              <p className="mt-3 font-open-sans text-xs text-gray-400">
                Yazıcıya doğrudan bağlanır, internet kesilse de çalışır, kendiliğinden güncellenir.
              </p>
            </div>
          </div>

          <ul className="mt-8 grid sm:grid-cols-2 lg:grid-cols-4 gap-3">
            {securityPractices.map((item) => (
              <li key={item.text} className="flex items-center gap-2">
                <Icon name={item.icon} size={16} className="text-green-500 flex-shrink-0" />
                <span className="font-open-sans text-xs text-gray-300">{item.text}</span>
              </li>
            ))}
          </ul>
        </div>

        {/* Stats */}
        <div className="mt-12 pt-8 border-t border-gray-800">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 text-center">
            <div>
              <p className="font-poppins font-heading-bold text-3xl text-primary mb-2">
                0 ₺
              </p>
              <p className="font-open-sans font-body-normal text-sm text-gray-300">
                QR menü — süresiz ücretsiz
              </p>
            </div>

            <div>
              <p className="font-poppins font-heading-bold text-3xl text-primary mb-2">
                Çevrimdışı
              </p>
              <p className="font-open-sans font-body-normal text-sm text-gray-300">
                internet kesilse de adisyon
              </p>
            </div>

            <div>
              <p className="font-poppins font-heading-bold text-3xl text-primary mb-2">
                3D/AR
              </p>
              <p className="font-open-sans font-body-normal text-sm text-gray-300">
                uygulamasız menü
              </p>
            </div>

            <div>
              <p className="font-poppins font-heading-bold text-3xl text-primary mb-2">
                Kuruş
              </p>
              <p className="font-open-sans font-body-normal text-sm text-gray-300">
                hassasiyetinde para hesabı
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Bottom Bar */}
      <div className="border-t border-gray-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex flex-col md:flex-row justify-between items-center space-y-4 md:space-y-0">
            {/* Copyright */}
            <div className="flex items-center space-x-4">
              <p className="font-open-sans font-body-normal text-sm text-gray-400">
                © {currentYear} Cafe Otomasyonu. Tüm hakları saklıdır.
              </p>
            </div>

            {/* Legal Links */}
            <div className="flex items-center space-x-6">
              <button
                onClick={() => handleLinkClick('/privacy-policy')}
                className="font-open-sans font-body-normal text-sm text-gray-400 hover:text-white transition-colors duration-300"
              >
                Gizlilik Politikası
              </button>
              <button
                onClick={() => handleLinkClick('/terms-of-service')}
                className="font-open-sans font-body-normal text-sm text-gray-400 hover:text-white transition-colors duration-300"
              >
                Kullanım Şartları
              </button>
              <button
                onClick={() => handleLinkClick('/cookie-policy')}
                className="font-open-sans font-body-normal text-sm text-gray-400 hover:text-white transition-colors duration-300"
              >
                Çerez Politikası
              </button>
            </div>

            {/* Language & Currency */}
            <div className="flex items-center space-x-4">
              <div className="flex items-center space-x-2">
                <Icon name="Globe" size={16} className="text-gray-400" />
                <span className="font-open-sans font-body-normal text-sm text-gray-400">
                  Türkçe
                </span>
              </div>
              <div className="flex items-center space-x-2">
                <Icon name="DollarSign" size={16} className="text-gray-400" />
                <span className="font-open-sans font-body-normal text-sm text-gray-400">
                  TRY (₺)
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>

    </footer>
  );
};

export default Footer;